import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  ChangeItem,
  ChangeItemAction,
  ChangeState,
  AddChangeItemRequest,
  UpdateChangeItemRequest,
  SearchResultItem,
  CreatePartRequest,
  CreateDocumentRequest,
  PartType,
  DocumentType,
  FileVaultItem,
  Part,
  Document,
  BomNode
} from '../../models/file-vault.models';

export interface AffectedItemsChangeEvent {
  action: 'add' | 'update' | 'remove';
  item: ChangeItem;
}

@Component({
  selector: 'fv-affected-items',
  standalone: true,
  template: `
    <div class="affected-items-container">
      <!-- Toolbar -->
      <div class="toolbar" *ngIf="!readonly">
        <div class="toolbar-left">
          <span class="title">Affected Items ({{ items.length }})</span>
        </div>
        <div class="toolbar-right">
          <button kendoButton [themeColor]="'primary'" (click)="openAddDialog()" [disabled]="!changeId">
            Add Item
          </button>
        </div>
      </div>

      <!-- Items Grid -->
      <kendo-grid
        [data]="gridData"
        [height]="gridHeight"
        class="items-grid">

        <kendo-grid-column field="entityType" title="Type" [width]="90">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="entity-type-badge" [ngClass]="getEntityTypeClass(dataItem.entityType)">
              {{ dataItem.entityType }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="entityNumber" title="Number" [width]="150">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="entity-number">{{ dataItem.entityNumber || dataItem.entityId }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="entityName" title="Name" [width]="200">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.entityName || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="action" title="Action" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="action-badge" [ngClass]="getActionClass(dataItem.action)">
              {{ getActionLabel(dataItem.action) }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Revision Change" [width]="130">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="revision-change" *ngIf="dataItem.fromRevision || dataItem.toRevision">
              <span class="from">{{ dataItem.fromRevision || '?' }}</span>
              <span class="arrow">→</span>
              <span class="to">{{ dataItem.toRevision || '?' }}</span>
            </span>
            <span *ngIf="!dataItem.fromRevision && !dataItem.toRevision">-</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="notes" title="Notes" [width]="150">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="notes" [title]="dataItem.notes">{{ dataItem.notes || '-' }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Actions" [width]="140" *ngIf="!readonly">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="action-buttons">
              <button kendoButton fillMode="flat" (click)="openEditDialog(dataItem)" title="Edit" aria-label="Edit">
                <kendo-icon name="pencil"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat"
                      *ngIf="canUploadRevision(dataItem)"
                      (click)="openUploadDialog(dataItem)" title="Upload revised file" aria-label="Upload revised file">
                <kendo-icon name="upload"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat"
                      (click)="confirmRemove(dataItem)" title="Remove" aria-label="Remove"
                      class="btn-danger">
                <kendo-icon name="trash"></kendo-icon>
              </button>
            </div>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Empty State -->
      <div class="empty-state" *ngIf="items.length === 0">
        <p>No affected items</p>
        <button kendoButton [look]="'flat'" (click)="openAddDialog()" *ngIf="!readonly" [disabled]="!changeId">
          Add First Item
        </button>
        <p class="no-change-hint" *ngIf="!changeId">A change must be created or selected before adding affected items.</p>
      </div>

      <!-- Add/Edit Dialog -->
      <kendo-dialog *ngIf="showDialog"
                    [title]="dialogTitle"
                    [width]="500"
                    (close)="closeDialog()">
        <div class="dialog-content">
          <!-- Entity Type -->
          <div class="form-group">
            <label>Entity Type *</label>
            <kendo-dropdownlist
              [(ngModel)]="editingItem.entityType"
              [data]="entityTypes"
              [valuePrimitive]="true"
              [disabled]="dialogMode === 'edit'">
            </kendo-dropdownlist>
          </div>

          <!-- Entity Search (Add mode) -->
          <div class="form-group" *ngIf="dialogMode === 'add'">
            <label>{{ editingItem.entityType }} *</label>
            <input kendoTextBox
                   [(ngModel)]="entitySearchText"
              [placeholder]="getEntityPlaceholder()"
              (input)="onEntitySearch()" />
            <div class="searching-indicator" *ngIf="isSearching">
              <kendo-loader type="infinite-spinner" size="small"></kendo-loader>
              <span>Searching...</span>
            </div>
            <div class="search-error" *ngIf="searchError">{{ searchError }}</div>
            <div class="search-results" *ngIf="searchResults.length > 0">
              <div class="search-result"
                   *ngFor="let result of searchResults"
                   (click)="selectEntity(result)">
                <span class="result-number">{{ result.identifier }}</span>
                <span class="result-name">{{ result.name }}</span>
              </div>
            </div>
            <div class="create-entity" *ngIf="editingItem.entityType !== 'File' && searchResults.length === 0 && entitySearchText.length >= 2 && !isGuid(entitySearchText) && !isSearching">
              <div class="create-title">
                {{ editingItem.entityType }} not found. Create a new one?
              </div>
              <div class="create-fields">
                <input kendoTextBox
                       [(ngModel)]="createEntityName"
                       [placeholder]="getCreateEntityPlaceholder()" />
                <button kendoButton [themeColor]="'primary'"
                        (click)="createEntity()"
                        [disabled]="!canCreateEntity || isCreatingEntity">
                  {{ isCreatingEntity ? 'Creating...' : 'Create' }}
                </button>
              </div>
            </div>
            <div class="selected-entity" *ngIf="editingItem.entityId">
              <strong>Selected:</strong> {{ editingItem.entityNumber }} - {{ editingItem.entityName || '(no name)' }}
            </div>
          </div>

          <!-- Action -->
          <div class="form-group">
            <label>Action *</label>
            <kendo-dropdownlist
              [(ngModel)]="editingItem.action"
              [data]="actions"
              textField="text"
              valueField="value"
              [valuePrimitive]="true">
            </kendo-dropdownlist>
          </div>

          <!-- Revision Change -->
          <div class="form-row">
            <div class="form-group half">
              <label>From Revision</label>
              <input kendoTextBox
                     [(ngModel)]="editingItem.fromRevision"
                     placeholder="e.g., A, 01" />
            </div>
            <div class="form-group half">
              <label>To Revision</label>
              <input kendoTextBox
                     [(ngModel)]="editingItem.toRevision"
                     placeholder="e.g., B, 02" />
            </div>
          </div>

          <!-- Notes -->
          <div class="form-group">
            <label>Notes</label>
            <textarea kendoTextArea
                      [(ngModel)]="editingItem.notes"
                      [rows]="2"
                      placeholder="Optional notes about this change...">
            </textarea>
          </div>

          <!-- Validation hint -->
          <div class="validation-hint" *ngIf="dialogMode === 'add' && !editingItem.entityId">
            Search for and select a {{ editingItem.entityType.toLowerCase() }} above before saving.
          </div>

          <!-- Save Error -->
          <div class="save-error" *ngIf="saveError">{{ saveError }}</div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialog()">Cancel</button>
          <button kendoButton [themeColor]="'primary'"
                  (click)="saveItem()"
                  [disabled]="!canSaveItem || isSaving">
            {{ isSaving ? 'Saving...' : (dialogMode === 'add' ? 'Add' : 'Save') }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Remove Confirmation -->
      <kendo-dialog *ngIf="showRemoveConfirm"
                    title="Remove Item"
                    [width]="400"
                    (close)="showRemoveConfirm = false">
        <p>Remove <strong>{{ itemToRemove?.entityNumber }}</strong> from affected items?</p>
        <kendo-dialog-actions>
          <button kendoButton (click)="showRemoveConfirm = false">Cancel</button>
          <button kendoButton [themeColor]="'error'" (click)="removeItem()">Remove</button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Where-Used Suggestion Dialog -->
      <kendo-dialog *ngIf="showWhereUsedDialog"
                    title="Related Items Found"
                    [width]="550"
                    (close)="dismissWhereUsed()">
        <div class="where-used-dialog">
          <p class="where-used-description">
            <strong>{{ whereUsedSourceLabel }}</strong> is used by {{ whereUsedSuggestions.length }}
            other item(s). Would you like to add them to this change?
          </p>

          <div class="where-used-list">
            <label class="where-used-select-all">
              <input type="checkbox"
                     [checked]="allWhereUsedSelected"
                     [indeterminate]="someWhereUsedSelected && !allWhereUsedSelected"
                     (change)="toggleAllWhereUsed($event)" />
              Select All
            </label>
            <div class="where-used-item" *ngFor="let suggestion of whereUsedSuggestions">
              <label class="where-used-check">
                <input type="checkbox" [(ngModel)]="suggestion.selected" />
                <span class="entity-type-badge" [ngClass]="suggestion.entityType.toLowerCase()">
                  {{ suggestion.entityType }}
                </span>
                <span class="wu-number">{{ suggestion.entityNumber }}</span>
                <span class="wu-name">{{ suggestion.entityName }}</span>
              </label>
              <span class="wu-revision" *ngIf="suggestion.currentRevision">
                {{ suggestion.currentRevision }} → {{ calculateNextRevision(suggestion.currentRevision) }}
              </span>
            </div>
          </div>

          <div class="where-used-hint" *ngIf="whereUsedDuplicateCount > 0">
            {{ whereUsedDuplicateCount }} item(s) already on this change were excluded.
          </div>

          <div class="where-used-error" *ngIf="whereUsedError">{{ whereUsedError }}</div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="dismissWhereUsed()">Skip</button>
          <button kendoButton [themeColor]="'primary'"
                  (click)="addSelectedWhereUsed()"
                  [disabled]="!hasWhereUsedSelections || isAddingWhereUsed">
            {{ isAddingWhereUsed ? 'Adding...' : 'Add Selected (' + whereUsedSelectedCount + ')' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Upload Revised File Dialog -->
      <kendo-dialog *ngIf="showUploadDialog"
                    [title]="'Upload Revised File'"
                    [width]="500"
                    (close)="closeUploadDialog()">
        <div class="upload-dialog-content">
          <div class="upload-target-info">
            <span class="entity-type-badge" [ngClass]="getEntityTypeClass(uploadItem?.entityType || '')">
              {{ uploadItem?.entityType }}
            </span>
            <strong>{{ uploadItem?.entityNumber || uploadItem?.entityId }}</strong>
            <span class="upload-action-badge" [ngClass]="getActionClass(uploadItem?.action!)">
              {{ getActionLabel(uploadItem?.action!) }}
            </span>
          </div>

          <div class="upload-revision-info" *ngIf="uploadVersionInfo">
            {{ uploadVersionInfo }}
          </div>

          <div class="form-group">
            <label>Select File *</label>
            <input type="file" class="file-input" (change)="onUploadFileSelected($event)" />
            <div class="file-selected" *ngIf="uploadFile">
              {{ uploadFile.name }} ({{ formatFileSize(uploadFile.size) }})
            </div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <input kendoTextBox
                   [(ngModel)]="uploadDescription"
                   placeholder="Brief description of changes..." />
          </div>

          <div class="upload-progress" *ngIf="isUploading">
            <kendo-loader type="infinite-spinner" size="small"></kendo-loader>
            <span>Uploading and attaching to latest version...</span>
          </div>

          <div class="upload-success" *ngIf="uploadSuccess">
            {{ uploadSuccess }}
          </div>

          <div class="upload-error" *ngIf="uploadError">
            {{ uploadError }}
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeUploadDialog()">{{ uploadSuccess ? 'Done' : 'Cancel' }}</button>
          <button kendoButton [themeColor]="'primary'"
                  *ngIf="!uploadSuccess"
                  (click)="uploadRevisedFile()"
                  [disabled]="!uploadFile || isUploading">
            {{ isUploading ? 'Uploading...' : 'Upload & Attach' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .affected-items-container {
      display: flex;
      flex-direction: column;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border);
    }

    .title {
      font-weight: 600;
      font-size: 14px;
    }

    .items-grid {
      font-size: 13px;
    }

    .entity-type-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .entity-type-badge.part { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .entity-type-badge.document { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .entity-type-badge.file { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }

    .entity-number {
      font-family: monospace;
      font-weight: 500;
    }

    .action-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;
    }

    .action-badge.add { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .action-badge.modify { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .action-badge.remove { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .action-badge.replace { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }

    .revision-change {
      display: flex;
      align-items: center;
      gap: 4px;
      font-family: monospace;
      font-size: 12px;
    }

    .revision-change .from { color: var(--fv-text-secondary); }
    .revision-change .arrow { color: var(--fv-text-muted); }
    .revision-change .to { color: var(--fv-status-success-fg); font-weight: 500; }

    .notes {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--fv-text-secondary);
    }

    .action-buttons {
      display: flex;
      gap: 4px;
    }

    .btn-danger { color: #dc3545; }

    .empty-state {
      padding: 32px;
      text-align: center;
      color: var(--fv-text-secondary);
      background: var(--fv-surface-alt);
    }

    .no-change-hint {
      margin-top: 8px;
      font-size: 12px;
      color: var(--fv-status-warning-fg);
    }

    .dialog-content {
      padding: 16px 0;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-row {
      display: flex;
      gap: 16px;
    }

    .form-group.half {
      flex: 1;
    }

    .form-group label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 13px;
    }

    .form-group input,
    .form-group textarea,
    .form-group kendo-dropdownlist {
      width: 100%;
    }

    .search-results {
      max-height: 150px;
      overflow-y: auto;
      border: 1px solid var(--fv-border);
      border-top: none;
      background: var(--fv-surface);
    }

    .search-result {
      padding: 8px 12px;
      cursor: pointer;
      display: flex;
      gap: 8px;
    }

    .search-result:hover {
      background: var(--fv-surface-hover);
    }

    .result-number {
      font-family: monospace;
      font-weight: 500;
    }

    .result-name {
      color: var(--fv-text-secondary);
    }

    .selected-entity {
      margin-top: 8px;
      padding: 8px;
      background: var(--fv-status-success-bg);
      border-radius: 4px;
      font-size: 13px;
    }

    .create-entity {
      margin-top: 8px;
      padding: 10px;
      border: 1px dashed var(--fv-border);
      border-radius: 4px;
      background: var(--fv-surface-alt);
    }

    .create-title {
      font-size: 12px;
      color: var(--fv-text-secondary);
      margin-bottom: 8px;
    }

    .create-fields {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .searching-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 6px;
      font-size: 12px;
      color: var(--fv-status-info-fg);
    }

    .search-error {
      margin-top: 6px;
      font-size: 12px;
      color: var(--fv-status-error-fg);
      padding: 6px 8px;
      background: var(--fv-status-error-bg);
      border-radius: 4px;
    }

    .save-error {
      margin-top: 8px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
      padding: 10px 12px;
      background: var(--fv-status-error-bg);
      border: 1px solid var(--fv-status-error-border);
      border-radius: 4px;
    }

    .validation-hint {
      font-size: 12px;
      color: var(--fv-status-warning-fg);
      padding: 8px 10px;
      background: var(--fv-status-warning-bg);
      border-radius: 4px;
      margin-top: 4px;
    }

    .where-used-dialog {
      padding: 8px 0;
    }

    .where-used-description {
      margin: 0 0 16px 0;
      color: var(--fv-text);
      line-height: 1.4;
    }

    .where-used-list {
      max-height: 280px;
      overflow-y: auto;
      border: 1px solid var(--fv-border);
      border-radius: 6px;
      background: var(--fv-surface);
    }

    .where-used-select-all {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      font-weight: 600;
      font-size: 13px;
      border-bottom: 1px solid var(--fv-border);
      background: var(--fv-surface-alt);
      cursor: pointer;
    }

    .where-used-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      border-bottom: 1px solid var(--fv-border-subtle);
    }

    .where-used-item:last-child {
      border-bottom: none;
    }

    .where-used-item:hover {
      background: var(--fv-surface-hover);
    }

    .where-used-check {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      flex: 1;
    }

    .wu-number {
      font-family: monospace;
      font-weight: 500;
      font-size: 13px;
    }

    .wu-name {
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .wu-revision {
      font-size: 11px;
      color: var(--fv-text-muted);
      white-space: nowrap;
    }

    .where-used-hint {
      margin-top: 10px;
      font-size: 12px;
      color: var(--fv-text-muted);
      font-style: italic;
    }

    .where-used-error {
      margin-top: 10px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
      padding: 8px 10px;
      background: var(--fv-status-error-bg);
      border-radius: 4px;
    }

    /* Upload dialog styles */
    .upload-dialog-content {
      padding: 8px 0;
    }

    .upload-target-info {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      background: var(--fv-surface-hover);
      border-radius: 6px;
      margin-bottom: 16px;
    }

    .upload-action-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;
      margin-left: auto;
    }

    .upload-revision-info {
      font-size: 12px;
      color: var(--fv-status-info-fg);
      background: var(--fv-status-info-bg);
      padding: 8px 12px;
      border-radius: 4px;
      margin-bottom: 16px;
    }

    .file-input {
      width: 100%;
      padding: 8px;
      border: 1px dashed var(--fv-dropzone-border);
      border-radius: 4px;
      cursor: pointer;
      background: var(--fv-dropzone-bg);
    }

    .file-input:hover {
      border-color: var(--fv-dropzone-active-border);
      background: var(--fv-dropzone-active-bg);
    }

    .file-selected {
      margin-top: 6px;
      font-size: 12px;
      color: var(--fv-status-success-fg);
      font-weight: 500;
    }

    .upload-progress {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 12px;
      font-size: 13px;
      color: var(--fv-status-info-fg);
      padding: 10px;
      background: var(--fv-status-info-bg);
      border-radius: 4px;
    }

    .upload-success {
      margin-top: 12px;
      font-size: 13px;
      color: var(--fv-status-success-fg);
      padding: 10px 12px;
      background: var(--fv-status-success-bg);
      border: 1px solid var(--fv-status-success-border);
      border-radius: 4px;
    }

    .upload-error {
      margin-top: 12px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
      padding: 10px 12px;
      background: var(--fv-status-error-bg);
      border: 1px solid var(--fv-status-error-border);
      border-radius: 4px;
    }
  `],
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule, DialogModule, InputsModule, DropDownsModule, IndicatorsModule, IconsModule, KENDO_ICONS]
})
export class AffectedItemsComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Change ID */
  @Input() changeId!: string;

  /** Pre-loaded items */
  @Input() items: ChangeItem[] = [];

  /** Read-only mode */
  @Input() readonly = false;

  /** Grid height */
  @Input() gridHeight = 250;

  /** Current state of the parent change (enables upload action during InImplementation) */
  @Input() changeState?: ChangeState;

  /** Emitted when items change */
  @Output() itemsChanged = new EventEmitter<AffectedItemsChangeEvent>();

  /** Emitted when items are loaded */
  @Output() itemsLoaded = new EventEmitter<ChangeItem[]>();

  isLoading = false;
  isSaving = false;

  // Dialog state
  showDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  editingItem: EditingItem = this.createEmptyItem();
  editingItemId?: string;

  // Entity search
  entitySearchText = '';
  searchResults: SearchResultItem[] = [];
  createEntityName = '';
  isCreatingEntity = false;
  isSearching = false;
  searchError = '';
  saveError = '';
  private searchTimeout?: ReturnType<typeof setTimeout>;

  // Remove confirmation
  showRemoveConfirm = false;
  itemToRemove?: ChangeItem;

  // Where-used suggestion state
  showWhereUsedDialog = false;
  whereUsedSuggestions: WhereUsedSuggestion[] = [];
  whereUsedSourceLabel = '';
  whereUsedDuplicateCount = 0;
  whereUsedError = '';
  isAddingWhereUsed = false;

  // Upload revised file state
  showUploadDialog = false;
  uploadItem?: ChangeItem;
  uploadFile?: File;
  uploadDescription = '';
  isUploading = false;
  uploadProgress = 0;
  uploadError = '';
  uploadSuccess = '';
  uploadVersionInfo = '';

  entityTypes = ['Part', 'Document', 'File'];

  actions = [
    { text: 'Add', value: ChangeItemAction.Add },
    { text: 'Modify', value: ChangeItemAction.Modify },
    { text: 'Remove', value: ChangeItemAction.Remove },
    { text: 'Replace', value: ChangeItemAction.Replace }
  ];

  get gridData(): GridDataResult {
    return {
      data: this.items,
      total: this.items.length
    };
  }

  get dialogTitle(): string {
    return this.dialogMode === 'add' ? 'Add Affected Item' : 'Edit Affected Item';
  }

  get canSaveItem(): boolean {
    if (this.dialogMode === 'add') {
      return !!this.editingItem.entityId && this.editingItem.entityType !== '';
    }
    return true;
  }

  ngOnInit(): void {
    if (this.changeId && this.items.length === 0) {
      this.loadItems();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeId'] && !changes['changeId'].firstChange) {
      this.loadItems();
    }
  }

  async loadItems(): Promise<void> {
    if (!this.changeId) return;

    this.isLoading = true;
    try {
      this.items = await this.api.getAffectedItems(this.changeId).toPromise() ?? [];
      await this.enrichItemDisplayNames();
      this.itemsLoaded.emit(this.items);
    } catch (error) {
      console.error('Failed to load affected items', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * For items missing entityNumber/entityName (typically File entities),
   * look up the details from the API so the grid shows a filename instead of a GUID.
   */
  private async enrichItemDisplayNames(): Promise<void> {
    const toEnrich = this.items.filter(
      item => !item.entityNumber && item.entityId
    );

    if (toEnrich.length === 0) return;

    const lookups = toEnrich.map(async (item) => {
      try {
        if (item.entityType === 'File') {
          const file = await this.api.getFile(item.entityId).toPromise();
          if (file) {
            item.entityNumber = this.getFileDisplayName(file);
            item.entityName = file.description || item.entityNumber;
          }
        } else if (item.entityType === 'Part') {
          const part = await this.api.getPart(item.entityId).toPromise();
          if (part) {
            item.entityNumber = part.partNumber;
            item.entityName = part.name;
          }
        } else if (item.entityType === 'Document') {
          const doc = await this.api.getDocument(item.entityId).toPromise();
          if (doc) {
            item.entityNumber = doc.documentNumber;
            item.entityName = doc.title;
          }
        }
      } catch (err) {
        console.warn(`Could not resolve display name for ${item.entityType} ${item.entityId}`, err);
      }
    });

    await Promise.all(lookups);
  }

  openAddDialog(): void {
    this.dialogMode = 'add';
    this.editingItem = this.createEmptyItem();
    this.entitySearchText = '';
    this.searchResults = [];
    this.createEntityName = '';
    this.isSearching = false;
    this.searchError = '';
    this.saveError = '';
    this.showDialog = true;
  }

  openEditDialog(item: ChangeItem): void {
    this.dialogMode = 'edit';
    this.editingItemId = item.id;
    this.editingItem = {
      entityType: item.entityType,
      entityId: item.entityId,
      entityNumber: item.entityNumber || '',
      entityName: item.entityName || '',
      action: item.action,
      fromRevision: item.fromRevision || '',
      toRevision: item.toRevision || '',
      notes: item.notes || ''
    };
    this.saveError = '';
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.editingItem = this.createEmptyItem();
    this.editingItemId = undefined;
    this.entitySearchText = '';
    this.searchResults = [];
    this.createEntityName = '';
    this.isSearching = false;
    this.searchError = '';
    this.saveError = '';
  }

  onEntitySearch(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchError = '';

    if (!this.entitySearchText || this.entitySearchText.length < 2) {
      this.searchResults = [];
      this.createEntityName = '';
      this.isSearching = false;
      return;
    }

    this.isSearching = true;

    // Debounce search
    this.searchTimeout = setTimeout(async () => {
      try {
        if (this.isGuid(this.entitySearchText)) {
          const id = this.entitySearchText.trim();
          this.editingItem.entityId = id;
          this.editingItem.entityNumber = id;
          this.editingItem.entityName = '';
          this.searchResults = [];

          // Direct ID lookup for all entity types
          if (this.editingItem.entityType === 'File') {
            const file = await this.api.getFile(id).toPromise();
            if (file) {
              const displayName = this.getFileDisplayName(file);
              this.editingItem.entityNumber = displayName;
              this.editingItem.entityName = file.description || displayName;
              const currentRev = file.revision || String(file.version ?? 0);
              this.editingItem.fromRevision = currentRev;
              this.editingItem.toRevision = this.calculateNextRevision(currentRev);
            }
          } else if (this.editingItem.entityType === 'Part') {
            const part = await this.api.getPart(id).toPromise();
            if (part) {
              this.editingItem.entityNumber = part.partNumber;
              this.editingItem.entityName = part.name;
              const currentRev = part.currentRevision || '0';
              this.editingItem.fromRevision = currentRev;
              this.editingItem.toRevision = this.calculateNextRevision(currentRev);
            }
          } else if (this.editingItem.entityType === 'Document') {
            const doc = await this.api.getDocument(id).toPromise();
            if (doc) {
              this.editingItem.entityNumber = doc.documentNumber;
              this.editingItem.entityName = doc.title;
              const currentRev = doc.currentRevision || '0';
              this.editingItem.fromRevision = currentRev;
              this.editingItem.toRevision = this.calculateNextRevision(currentRev);
            }
          }

          this.isSearching = false;
          return;
        }

        if (this.editingItem.entityType === 'Part') {
          const results = await this.api.searchParts(this.entitySearchText, 1, 10).toPromise();
          this.searchResults = results?.items ?? [];
        } else if (this.editingItem.entityType === 'Document') {
          const results = await this.api.searchDocuments(this.entitySearchText, 1, 10).toPromise();
          this.searchResults = results?.items ?? [];
        } else if (this.editingItem.entityType === 'File') {
          const results = await this.api.searchFiles({
            searchText: this.entitySearchText,
            page: 1,
            pageSize: 10
          }).toPromise();
          this.searchResults = (results?.items ?? []).map(file => this.mapFileToSearchResult(file));
        } else {
          this.searchResults = [];
        }
      } catch (error: any) {
        console.error('Failed to search entities', error);
        this.searchResults = [];
        this.searchError = error?.message || 'Search failed. Check that the API is available.';
      } finally {
        this.isSearching = false;
      }
    }, 300);
  }

  selectEntity(entity: SearchResultItem): void {
    this.editingItem.entityId = entity.entityId;
    this.editingItem.entityNumber = entity.identifier;
    this.editingItem.entityName = entity.name;
    this.searchResults = [];
    this.entitySearchText = '';
    this.createEntityName = '';

    // Auto-populate revision fields from the selected entity
    this.autoPopulateRevision(entity.entityId);
  }

  isGuid(value: string): boolean {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value.trim());
  }

  get canCreateEntity(): boolean {
    return (this.editingItem.entityType === 'Part' || this.editingItem.entityType === 'Document') &&
      !!this.entitySearchText.trim() &&
      !!this.createEntityName.trim();
  }

  getEntityPlaceholder(): string {
    if (this.editingItem.entityType === 'Part') return 'Search by part number or name...';
    if (this.editingItem.entityType === 'Document') return 'Search by document number or title...';
    return 'Search by file name...';
  }

  getCreateEntityPlaceholder(): string {
    if (this.editingItem.entityType === 'Part') return 'Part name';
    if (this.editingItem.entityType === 'Document') return 'Document title';
    return '';
  }

  private mapFileToSearchResult(file: FileVaultItem): SearchResultItem {
    const displayName = this.getFileDisplayName(file);
    return {
      entityType: 'File',
      entityId: file.id,
      identifier: displayName,
      name: file.description || displayName,
      description: file.description || '',
      createdAt: file.createdAt,
      modifiedAt: file.modifiedAt,
      createdBy: file.createdBy || '',
      score: 0
    };
  }

  private getFileDisplayName(file: FileVaultItem): string {
    return file.fullFileName || `${file.fileName}${file.fileExtension ?? ''}`;
  }

  async createEntity(): Promise<void> {
    if (!this.canCreateEntity) return;

    this.isCreatingEntity = true;
    try {
      if (this.editingItem.entityType === 'Part') {
        const request: CreatePartRequest = {
          partNumber: this.entitySearchText.trim(),
          name: this.createEntityName.trim(),
          partType: PartType.Component
        };
        const part = await this.api.createPart(request).toPromise();
        if (part) {
          this.editingItem.entityId = part.id;
          this.editingItem.entityNumber = part.partNumber;
          this.editingItem.entityName = part.name;
          const currentRev = part.currentRevision || '0';
          this.editingItem.fromRevision = currentRev;
          this.editingItem.toRevision = this.calculateNextRevision(currentRev);
        }
      } else if (this.editingItem.entityType === 'Document') {
        const request: CreateDocumentRequest = {
          documentNumber: this.entitySearchText.trim(),
          title: this.createEntityName.trim(),
          documentType: DocumentType.Other
        };
        const document = await this.api.createDocument(request).toPromise();
        if (document) {
          this.editingItem.entityId = document.id;
          this.editingItem.entityNumber = document.documentNumber;
          this.editingItem.entityName = document.title;
          const currentRev = document.currentRevision || '0';
          this.editingItem.fromRevision = currentRev;
          this.editingItem.toRevision = this.calculateNextRevision(currentRev);
        }
      }

      this.searchResults = [];
      this.entitySearchText = '';
      this.createEntityName = '';
    } catch (error) {
      console.error('Failed to create entity', error);
    } finally {
      this.isCreatingEntity = false;
    }
  }

  async saveItem(): Promise<void> {
    if (!this.canSaveItem || !this.changeId) {
      if (!this.editingItem.entityId) {
        this.saveError = 'Please search for and select an entity before saving.';
      } else if (!this.changeId) {
        this.saveError = 'No change ID provided. Cannot save affected item.';
      }
      return;
    }

    this.isSaving = true;
    this.saveError = '';
    try {
      if (this.dialogMode === 'add') {
        const request: AddChangeItemRequest = {
          entityType: this.editingItem.entityType,
          entityId: this.editingItem.entityId,
          action: this.editingItem.action,
          fromRevision: this.editingItem.fromRevision || undefined,
          toRevision: this.editingItem.toRevision || undefined,
          notes: this.editingItem.notes || undefined
        };

        const addedEntityType = this.editingItem.entityType;
        const addedEntityId = this.editingItem.entityId;
        const addedEntityLabel = this.editingItem.entityNumber || this.editingItem.entityName || addedEntityId;

        const newItem = await this.api.addAffectedItem(this.changeId, request).toPromise();
        if (newItem) {
          this.items = [...this.items, newItem];
          this.itemsChanged.emit({ action: 'add', item: newItem });
        }

        this.closeDialog();

        // After successfully adding, check for where-used relationships
        await this.checkWhereUsed(addedEntityType, addedEntityId, addedEntityLabel);
      } else if (this.editingItemId) {
        const request: UpdateChangeItemRequest = {
          action: this.editingItem.action,
          fromRevision: this.editingItem.fromRevision || undefined,
          toRevision: this.editingItem.toRevision || undefined,
          notes: this.editingItem.notes || undefined
        };

        const updatedItem = await this.api.updateAffectedItem(this.changeId, this.editingItemId, request).toPromise();
        if (updatedItem) {
          this.items = this.items.map(i => i.id === this.editingItemId ? updatedItem : i);
          this.itemsChanged.emit({ action: 'update', item: updatedItem });
        }

        this.closeDialog();
      }
    } catch (error: any) {
      console.error('Failed to save item', error);
      this.saveError = error?.error?.message || error?.error?.error || error?.message || 'Failed to save. Please check the API connection and try again.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmRemove(item: ChangeItem): void {
    this.itemToRemove = item;
    this.showRemoveConfirm = true;
  }

  async removeItem(): Promise<void> {
    if (!this.itemToRemove || !this.changeId) return;

    try {
      await this.api.removeAffectedItem(this.changeId, this.itemToRemove.id).toPromise();
      this.items = this.items.filter(i => i.id !== this.itemToRemove!.id);
      this.itemsChanged.emit({ action: 'remove', item: this.itemToRemove });
    } catch (error) {
      console.error('Failed to remove item', error);
    } finally {
      this.showRemoveConfirm = false;
      this.itemToRemove = undefined;
    }
  }

  private createEmptyItem(): EditingItem {
    return {
      entityType: 'Part',
      entityId: '',
      entityNumber: '',
      entityName: '',
      action: ChangeItemAction.Modify,
      fromRevision: '',
      toRevision: '',
      notes: ''
    };
  }

  /**
   * Calculate the next revision string.
   * Numeric revisions (e.g. "0", "1") advance to "A".
   * Alpha revisions increment: "A"→"B", "Z"→"AA", "AZ"→"BA", "ZZ"→"AAA".
   */
  calculateNextRevision(current: string): string {
    if (!current) return 'A';

    const trimmed = current.trim().toUpperCase();

    // Numeric revision → first alpha revision
    if (/^\d+$/.test(trimmed)) {
      return 'A';
    }

    // Pure alpha revision → increment like a base-26 counter (A=1..Z=26)
    if (/^[A-Z]+$/.test(trimmed)) {
      const chars = trimmed.split('');
      let carry = true;
      for (let i = chars.length - 1; i >= 0 && carry; i--) {
        const code = chars[i].charCodeAt(0) - 64; // A=1, Z=26
        if (code < 26) {
          chars[i] = String.fromCharCode(64 + code + 1);
          carry = false;
        } else {
          chars[i] = 'A';
          // carry remains true
        }
      }
      if (carry) {
        chars.unshift('A'); // overflow: ZZ → AAA
      }
      return chars.join('');
    }

    // Mixed or unrecognised format — cannot safely calculate, leave blank
    return '';
  }

  /**
   * Fetch the current revision for the selected entity and auto-populate
   * fromRevision / toRevision on the editing item.
   */
  private async autoPopulateRevision(entityId: string): Promise<void> {
    try {
      let currentRev = '';

      if (this.editingItem.entityType === 'Part') {
        const part = await this.api.getPart(entityId).toPromise();
        currentRev = part?.currentRevision || '0';
      } else if (this.editingItem.entityType === 'Document') {
        const doc = await this.api.getDocument(entityId).toPromise();
        currentRev = doc?.currentRevision || '0';
      } else if (this.editingItem.entityType === 'File') {
        const file = await this.api.getFile(entityId).toPromise();
        currentRev = file?.revision || String(file?.version ?? 0);
      }

      if (currentRev) {
        this.editingItem.fromRevision = currentRev;
        this.editingItem.toRevision = this.calculateNextRevision(currentRev);
      }
    } catch (err) {
      console.warn('Could not auto-populate revision for', this.editingItem.entityType, entityId, err);
    }
  }

  getEntityTypeClass(type: string): string {
    return type.toLowerCase();
  }

  getActionLabel(action: ChangeItemAction): string {
    switch (action) {
      case ChangeItemAction.Add: return 'Add';
      case ChangeItemAction.Modify: return 'Modify';
      case ChangeItemAction.Remove: return 'Remove';
      case ChangeItemAction.Replace: return 'Replace';
      default: return 'Unknown';
    }
  }

  getActionClass(action: ChangeItemAction): string {
    switch (action) {
      case ChangeItemAction.Add: return 'add';
      case ChangeItemAction.Modify: return 'modify';
      case ChangeItemAction.Remove: return 'remove';
      case ChangeItemAction.Replace: return 'replace';
      default: return '';
    }
  }

  // ============ Upload Revised File ============

  /**
   * Whether a given affected item supports file upload.
   * Only Modify/Replace actions on Part/Document entities during InImplementation state.
   */
  canUploadRevision(item: ChangeItem): boolean {
    if (this.changeState !== ChangeState.InImplementation) return false;
    if (item.action !== ChangeItemAction.Modify && item.action !== ChangeItemAction.Replace) return false;
    return item.entityType === 'Part' || item.entityType === 'Document';
  }

  openUploadDialog(item: ChangeItem): void {
    this.uploadItem = item;
    this.uploadFile = undefined;
    this.uploadDescription = '';
    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadError = '';
    this.uploadSuccess = '';
    this.uploadVersionInfo = '';
    this.showUploadDialog = true;

    // Pre-fetch latest version info
    this.fetchLatestVersionInfo(item);
  }

  closeUploadDialog(): void {
    this.showUploadDialog = false;
    this.uploadItem = undefined;
    this.uploadFile = undefined;
    this.uploadDescription = '';
    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadError = '';
    this.uploadSuccess = '';
    this.uploadVersionInfo = '';
  }

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadFile = input.files[0];
      this.uploadError = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Upload the selected file and attach it to the latest InWork version
   * of the affected Part or Document.
   */
  async uploadRevisedFile(): Promise<void> {
    if (!this.uploadFile || !this.uploadItem) return;

    this.isUploading = true;
    this.uploadError = '';
    this.uploadSuccess = '';

    try {
      const item = this.uploadItem;
      const file = this.uploadFile;
      const description = this.uploadDescription || `Revised file for change ${this.changeId}`;

      if (item.entityType === 'Part') {
        // Get latest part version (should be the InWork revision created by change implementation)
        const version = await this.api.getLatestPartVersion(item.entityId).toPromise();
        if (!version) {
          this.uploadError = 'No version found for this part. Has the change been started for implementation?';
          return;
        }

        // Attach file to the version
        const attachedFile = await this.api.attachFileToPartVersion(version.id, file, description).toPromise();
        if (attachedFile) {
          this.uploadSuccess = `File "${file.name}" attached to ${item.entityNumber || 'Part'} version ${version.version} (Rev ${version.revision}). `
            + `File ID: ${attachedFile.id}`;
        }
      } else if (item.entityType === 'Document') {
        // Get latest document version
        const version = await this.api.getLatestDocumentVersion(item.entityId).toPromise();
        if (!version) {
          this.uploadError = 'No version found for this document. Has the change been started for implementation?';
          return;
        }

        // Attach file to the version
        const attachedFile = await this.api.attachFileToDocumentVersion(version.id, file, description).toPromise();
        if (attachedFile) {
          this.uploadSuccess = `File "${file.name}" attached to ${item.entityNumber || 'Document'} version ${version.versionNumber} (Rev ${version.revision}). `
            + `File ID: ${attachedFile.id}`;
        }
      }

      // Update the toRevision on the affected item if not already set
      if (this.uploadSuccess && item.id && !item.toRevision) {
        try {
          let newRevision = '';
          if (item.entityType === 'Part') {
            const v = await this.api.getLatestPartVersion(item.entityId).toPromise();
            if (v) newRevision = v.revision;
          } else if (item.entityType === 'Document') {
            const v = await this.api.getLatestDocumentVersion(item.entityId).toPromise();
            if (v) newRevision = v.revision;
          }

          if (newRevision) {
            const updateReq: UpdateChangeItemRequest = { toRevision: newRevision };
            const updated = await this.api.updateAffectedItem(this.changeId, item.id, updateReq).toPromise();
            if (updated) {
              this.items = this.items.map(i => i.id === item.id ? updated : i);
              this.itemsChanged.emit({ action: 'update', item: updated });
            }
          }
        } catch (err) {
          console.warn('Could not auto-update toRevision', err);
        }
      }
    } catch (error: any) {
      console.error('Upload failed', error);
      const body = error?.error;
      this.uploadError = body?.message || body?.title || error?.message || 'Upload failed. Please try again.';
    } finally {
      this.isUploading = false;
    }
  }

  /**
   * Pre-fetch and display the latest version info for context in the upload dialog.
   */
  private async fetchLatestVersionInfo(item: ChangeItem): Promise<void> {
    try {
      if (item.entityType === 'Part') {
        const version = await this.api.getLatestPartVersion(item.entityId).toPromise();
        if (version) {
          this.uploadVersionInfo = `Latest version: ${version.version} (Rev ${version.revision}) — `
            + `State: ${this.getLifecycleLabel(version.lifecycleState)}. `
            + `File will be attached to this version.`;
        } else {
          this.uploadVersionInfo = 'No versions found. A revision must be created first.';
        }
      } else if (item.entityType === 'Document') {
        const version = await this.api.getLatestDocumentVersion(item.entityId).toPromise();
        if (version) {
          this.uploadVersionInfo = `Latest version: ${version.versionNumber} (Rev ${version.revision}) — `
            + `State: ${this.getLifecycleLabel(version.state)}. `
            + `File will be attached to this version.`;
        } else {
          this.uploadVersionInfo = 'No versions found. A revision must be created first.';
        }
      }
    } catch (err) {
      this.uploadVersionInfo = 'Could not fetch version info.';
      console.warn('Failed to fetch version info for upload dialog', err);
    }
  }

  private getLifecycleLabel(state: number): string {
    switch (state) {
      case 0: return 'In Work';
      case 1: return 'In Review';
      case 2: return 'Approved';
      case 3: return 'Released';
      case 4: return 'Superseded';
      case 5: return 'Obsolete';
      default: return 'Unknown';
    }
  }

  // ============ Where-Used Auto-Population ============

  get allWhereUsedSelected(): boolean {
    return this.whereUsedSuggestions.length > 0 && this.whereUsedSuggestions.every(s => s.selected);
  }

  get someWhereUsedSelected(): boolean {
    return this.whereUsedSuggestions.some(s => s.selected);
  }

  get hasWhereUsedSelections(): boolean {
    return this.whereUsedSuggestions.some(s => s.selected);
  }

  get whereUsedSelectedCount(): number {
    return this.whereUsedSuggestions.filter(s => s.selected).length;
  }

  /**
   * After adding an affected item, check if the entity is used by other parts/assemblies
   * and offer to add them to the change as well.
   */
  async checkWhereUsed(entityType: string, entityId: string, entityLabel: string): Promise<void> {
    try {
      let relatedParts: Part[] = [];

      if (entityType === 'Part') {
        // Use BOM where-used to find parent assemblies
        const whereUsedNodes = await this.api.getWhereUsed(entityId, { maxDepth: 1 }).toPromise();
        relatedParts = (whereUsedNodes ?? []).map(node => node.part).filter((p): p is Part => !!p);
      } else if (entityType === 'Document') {
        // Find parts linked to this document
        relatedParts = await this.api.getDocumentLinkedParts(entityId).toPromise() ?? [];
      } else if (entityType === 'File') {
        // Find parts linked to this file
        relatedParts = await this.api.getFileLinkedParts(entityId).toPromise() ?? [];
      }

      if (relatedParts.length === 0) return;

      // Filter out items already on this change
      const existingEntityIds = new Set(this.items.map(i => i.entityId));
      const newParts = relatedParts.filter(p => !existingEntityIds.has(p.id));
      const duplicateCount = relatedParts.length - newParts.length;

      if (newParts.length === 0) return;

      // Build suggestion list
      this.whereUsedSuggestions = newParts.map(part => ({
        entityType: 'Part',
        entityId: part.id,
        entityNumber: part.partNumber,
        entityName: part.name || '',
        currentRevision: part.currentRevision,
        lifecycleState: part.lifecycleState,
        selected: true // default to selected
      }));

      this.whereUsedSourceLabel = entityLabel;
      this.whereUsedDuplicateCount = duplicateCount;
      this.whereUsedError = '';
      this.showWhereUsedDialog = true;
    } catch (error) {
      // Silently fail — where-used is a convenience feature, not critical
      console.warn('Where-used lookup failed for', entityType, entityId, error);
    }
  }

  toggleAllWhereUsed(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.whereUsedSuggestions.forEach(s => s.selected = checked);
  }

  dismissWhereUsed(): void {
    this.showWhereUsedDialog = false;
    this.whereUsedSuggestions = [];
    this.whereUsedSourceLabel = '';
    this.whereUsedDuplicateCount = 0;
    this.whereUsedError = '';
  }

  /**
   * Batch-add all selected where-used suggestions as affected items on this change.
   */
  async addSelectedWhereUsed(): Promise<void> {
    const selected = this.whereUsedSuggestions.filter(s => s.selected);
    if (selected.length === 0 || !this.changeId) return;

    this.isAddingWhereUsed = true;
    this.whereUsedError = '';

    let addedCount = 0;
    const errors: string[] = [];

    for (const suggestion of selected) {
      try {
        const currentRev = suggestion.currentRevision || '0';
        const request: AddChangeItemRequest = {
          entityType: suggestion.entityType,
          entityId: suggestion.entityId,
          action: ChangeItemAction.Modify,
          fromRevision: currentRev,
          toRevision: this.calculateNextRevision(currentRev),
          notes: `Auto-added via where-used (related to ${this.whereUsedSourceLabel})`
        };

        const newItem = await this.api.addAffectedItem(this.changeId, request).toPromise();
        if (newItem) {
          this.items = [...this.items, newItem];
          this.itemsChanged.emit({ action: 'add', item: newItem });
          addedCount++;
        }
      } catch (error: any) {
        const msg = error?.error?.message || error?.message || 'Unknown error';
        errors.push(`${suggestion.entityNumber}: ${msg}`);
        console.error(`Failed to add where-used item ${suggestion.entityNumber}`, error);
      }
    }

    this.isAddingWhereUsed = false;

    if (errors.length > 0) {
      this.whereUsedError = `Added ${addedCount} of ${selected.length} items. Errors: ${errors.join('; ')}`;
    } else {
      this.dismissWhereUsed();
    }
  }
}

interface EditingItem {
  entityType: string;
  entityId: string;
  entityNumber: string;
  entityName: string;
  action: ChangeItemAction;
  fromRevision: string;
  toRevision: string;
  notes: string;
}

interface WhereUsedSuggestion {
  entityType: string;
  entityId: string;
  entityNumber: string;
  entityName: string;
  currentRevision?: string;
  lifecycleState?: number;
  selected: boolean;
}
